/**
 * Wire the Figma media wishlist heart to the SWishlist (or similar) app button.
 * Relocates the app control under the styled heart for clicks, and mirrors the
 * app's "added" state so the visible heart fills when wishlisted.
 */
(function () {
  if (window.__srmWishlistWired) return;
  window.__srmWishlistWired = true;

  const HOST_SELECTOR = '[data-srm-wishlist]';
  const PAGE_SELECTOR = '.srm-product-page';
  const CARD_WISHLIST_SCOPE = '.places-to-start, .more-to-explore, .related-products, .template-search';
  const ACTIVE_CLASS = 'srm-wishlist-button--active';

  // Classes used by SWishlist / Swym / common wishlist apps when an item is saved.
  const ACTIVE_MARKERS = [
    'st-is-added',
    'swym-added',
    'swym-added-custom',
    'swym-hl-product-wishlisted',
    'is-added',
    'wishlisted',
    'added-to-wishlist',
    'is-wishlisted',
  ];

  function isOurHost(el) {
    return Boolean(el && (el.matches?.(HOST_SELECTOR) || el.closest?.(HOST_SELECTOR)));
  }

  function isHeaderWishlist(el) {
    return Boolean(el && el.closest?.('.header__action--wishlist, .section-header'));
  }

  function textMatch(el) {
    const text = `${el.textContent || ''} ${el.getAttribute('aria-label') || ''} ${el.getAttribute('title') || ''}`.toLowerCase();
    const cls = `${el.className || ''} ${el.id || ''}`.toLowerCase();
    return (
      text.includes('wishlist') ||
      text.includes('wish list') ||
      cls.includes('wishlist') ||
      cls.includes('swish')
    );
  }

  function isExcluded(el) {
    return (
      !el ||
      isOurHost(el) ||
      isHeaderWishlist(el) ||
      el.matches(
        'product-form, .product-form, .product-form__submit, .product-form__buttons, .quantity, .quantity__button, .quantity__input, .shopify-payment-button'
      )
    );
  }

  function movableRoot(control) {
    const buyButtons = control.closest('.product-buy-buttons');
    if (buyButtons) {
      let node = control;
      while (node.parentElement && node.parentElement !== buyButtons) {
        node = node.parentElement;
      }
      if (node.parentElement === buyButtons && !isExcluded(node)) return node;
    }

    const info = control.closest('.product__info-container');
    if (info && control.parentElement === info && !isExcluded(control)) return control;

    return control;
  }

  function hostControl(host) {
    if (!host) return null;
    return (
      host.querySelector('.srm-wishlist-proxy') ||
      host.querySelector('.st-wishlist-button') ||
      host.querySelector('.shopify-app-block') ||
      host.querySelector('[class*="wishlist"]:not(.srm-wishlist-button):not(.srm-wishlist-button__icon)') ||
      host.querySelector('[class*="swish"], [class*="Swish"]')
    );
  }

  function matchesProductId(node, productId) {
    if (!productId || !node) return true;
    const id = String(productId);
    const attrs = ['data-product-id', 'data-id', 'data-productid', 'data-handle'];
    const nodes = [node, ...node.querySelectorAll('*')];
    for (const el of nodes) {
      if (!(el instanceof Element)) continue;
      for (const attr of attrs) {
        const value = el.getAttribute(attr);
        if (value && value === id) return true;
      }
    }
    return false;
  }

  function findCardWishlistControl(host) {
    const productId = host.getAttribute('data-product-id');
    const card = host.closest(
      '.places-to-start__card, .places-to-start__slide, [data-pts-slide], .card-wrapper, .product-card-wrapper, .card, .grid__item'
    );
    if (!card) return hostControl(host);

    const existing = hostControl(host);
    if (existing) return existing;

    const nodes = card.querySelectorAll(
      '.st-wishlist-button, .st-wishlist-initialized, .shopify-app-block, [class*="wishlist"], [class*="swish"], [class*="Swish"]'
    );
    for (const node of nodes) {
      if (isOurHost(node) || isHeaderWishlist(node)) continue;
      if (node.closest(HOST_SELECTOR) && node.closest(HOST_SELECTOR) !== host) continue;
      if (productId && !matchesProductId(node, productId)) continue;
      return node;
    }

    if (productId) {
      const loose = document.querySelectorAll(
        `.st-wishlist-button[data-product-id="${productId}"], [data-product-id="${productId}"][class*="wishlist"]`
      );
      for (const node of loose) {
        if (isHeaderWishlist(node)) continue;
        const otherHost = node.closest(HOST_SELECTOR);
        if (otherHost && otherHost !== host) continue;
        return node;
      }
    }

    return null;
  }

  function findAppWishlistControl(root) {
    if (!root) return null;

    const selectors = [
      '.product-buy-buttons > *:not(product-form):not(.product-form)',
      '.product__info-container .shopify-app-block',
      '.product__info-container [class*="wishlist"]',
      '.product__info-container [class*="Wishlist"]',
      '.product__info-container [class*="swish"]',
      '.product__info-container [class*="Swish"]',
      '.product-buy-buttons [class*="wishlist"]',
      '.product-buy-buttons [class*="swish"]',
      '.product-buy-buttons button',
      '.product-buy-buttons a',
      '.srm-purchase-row button',
      '.srm-purchase-row a',
    ];

    const seen = new Set();

    for (const selector of selectors) {
      const nodes = root.querySelectorAll(selector);
      for (const node of nodes) {
        if (seen.has(node)) continue;
        seen.add(node);
        if (isExcluded(node)) continue;

        const matchesAppShell = node.classList.contains('shopify-app-block');
        const matchesSiblingInject =
          node.parentElement?.classList?.contains('product-buy-buttons') && !isExcluded(node);

        if (!(textMatch(node) || matchesAppShell || matchesSiblingInject)) continue;

        return movableRoot(node);
      }
    }

    return null;
  }

  function controlLooksActive(control) {
    if (!control) return false;

    const nodes = [control, ...control.querySelectorAll('*')];
    for (const node of nodes) {
      if (!(node instanceof Element)) continue;

      for (const marker of ACTIVE_MARKERS) {
        if (node.classList.contains(marker)) return true;
      }

      if (node.getAttribute('aria-pressed') === 'true') return true;
      if (node.getAttribute('aria-checked') === 'true') return true;
      if (node.getAttribute('data-wishlisted') === 'true') return true;
      if (node.getAttribute('data-added') === 'true') return true;
      if (node.getAttribute('data-is-added') === 'true') return true;

      const label = `${node.getAttribute('aria-label') || ''} ${node.getAttribute('title') || ''}`.toLowerCase();
      if (label.includes('remove from wishlist') || label.includes('remove wishlist')) return true;
    }

    return false;
  }

  function syncActiveState(host) {
    if (!host) return;
    const control = hostControl(host);
    const active = controlLooksActive(control);
    host.classList.toggle(ACTIVE_CLASS, active);
    host.setAttribute('aria-pressed', active ? 'true' : 'false');
  }

  function scheduleSync(host) {
    [0, 50, 150, 400, 800, 1600].forEach((ms) => {
      window.setTimeout(() => syncActiveState(host), ms);
    });
  }

  function watchState(host) {
    if (!host || host.dataset.srmWishlistWatching === 'true') return;
    host.dataset.srmWishlistWatching = 'true';

    const control = hostControl(host) || host;

    syncActiveState(host);

    const observer = new MutationObserver(() => syncActiveState(host));
    observer.observe(control, {
      attributes: true,
      attributeFilter: ['class', 'aria-pressed', 'aria-checked', 'aria-label', 'title', 'data-wishlisted', 'data-added', 'data-is-added'],
      childList: true,
      subtree: true,
    });

    host.addEventListener('click', (event) => {
      if (host.closest(CARD_WISHLIST_SCOPE)) {
        event.preventDefault();
        event.stopPropagation();
      }
      scheduleSync(host);
    });
  }

  function mount(host) {
    if (!host) return false;

    if (host.dataset.srmWishlistMounted === 'true') {
      watchState(host);
      syncActiveState(host);
      return true;
    }

    const page = host.closest(PAGE_SELECTOR) || document.querySelector(PAGE_SELECTOR);
    const control = host.closest(CARD_WISHLIST_SCOPE)
      ? findCardWishlistControl(host)
      : findAppWishlistControl(page) || hostControl(host);
    if (!control || host.contains(control)) {
      // Still watch in case the app injects later into the host.
      if (host.contains(control)) {
        control.classList.add('srm-wishlist-proxy');
        host.dataset.srmWishlistMounted = 'true';
        host.classList.add('srm-wishlist-button--live');
        watchState(host);
        return true;
      }
      return false;
    }

    const icon = host.querySelector('.srm-wishlist-button__icon');
    host.appendChild(control);
    control.classList.add('srm-wishlist-proxy');
    if (icon) host.appendChild(icon);

    const focusable =
      control.matches('button, a, [role="button"], input')
        ? control
        : control.querySelector('button, a, [role="button"], input') || control;

    if (focusable && !focusable.getAttribute('aria-label')) {
      focusable.setAttribute('aria-label', host.getAttribute('data-wishlist-label') || 'Add to wishlist');
    }

    host.dataset.srmWishlistMounted = 'true';
    host.classList.add('srm-wishlist-button--live');
    watchState(host);
    scheduleSync(host);
    return true;
  }

  function init() {
    const hosts = document.querySelectorAll(HOST_SELECTOR);
    if (!hosts.length) return;

    let allMounted = true;
    hosts.forEach((host) => {
      if (mount(host)) {
        scheduleSync(host);
      } else {
        allMounted = false;
      }
    });

    const page = document.querySelector(PAGE_SELECTOR);
    const section = document.querySelector('[data-places-to-start], .places-to-start, .more-to-explore, .related-products-section');
    const persistObserve = Boolean(document.querySelector('.more-to-explore, .related-products-section, .template-search'));
    const root = persistObserve ? document.body : page || section || document.body;
    if (!root || root.dataset.srmWishlistObserving === 'true') return;
    if (allMounted && !persistObserve) return;

    root.dataset.srmWishlistObserving = 'true';

    const observer = new MutationObserver(() => {
      document.querySelectorAll(HOST_SELECTOR).forEach((host) => {
        if (host.dataset.srmWishlistMounted === 'true') {
          watchState(host);
          return;
        }
        if (mount(host)) scheduleSync(host);
      });
    });

    observer.observe(root, { childList: true, subtree: true });
    if (!persistObserve) {
      window.setTimeout(() => observer.disconnect(), 15000);
    }
  }

  // SWishlist / Swym-style document events (when present)
  ['swishlist:st-wishlist-add', 'swishlist:st-wishlist-remove', 'swym:added-to-wishlist', 'swym:removed-from-wishlist'].forEach(
    (eventName) => {
      document.addEventListener(eventName, () => {
        document.querySelectorAll(HOST_SELECTOR).forEach((host) => scheduleSync(host));
      });
    }
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('srm:wishlist:refresh', init);
})();
